// Lifecycle boundary for the page's existing SFU and the server-authorized Agora provider.
// The Office socket selects the provider and supplies full grants; URL/client choices cannot authorize Agora.
import type { UID } from 'agora-rtc-sdk-ng';
import type { AgoraPageGrant, createAgoraPageProvider } from './agora-page';
type Page = ReturnType<typeof createAgoraPageProvider>;
export interface VoiceProviderOptions {
  startSfu: () => Promise<void>;
  beforeAgora: () => void;
  createAgora: () => Promise<Page>;
}
export function createVoiceProvider(options: VoiceProviderOptions) {
  let generation = 0, mode: 'sfu' | 'agora' | 'agora-down' = 'sfu';
  let page: Page | null = null;
  const close = (p: Page | null) => { if (p) void p.close().catch(() => {}); };
  function invalidate() {
    generation++;
    if (mode !== 'sfu') { mode = 'agora-down'; const old = page; page = null; close(old); }
  }
  async function selectAgora(grant: AgoraPageGrant) {
    if (!grant) throw new Error('Missing server authorization');
    const g = ++generation, old = page; page = null; mode = 'agora';
    // close() immediately gates/releases local mic and output, then requests channel leave. It may still be awaiting
    // SDK leave when the authorized destination starts: the server/provider revocation window is not a local fence.
    close(old);
    let p: Page | null = null;
    try {
      options.beforeAgora();
      p = await options.createAgora();
      if (g !== generation) { close(p); return; }
      // A cancelled pre-join permission prompt can hold createAgora() until after this grant expires.
      if (!Number.isFinite(grant.lease.expiresAt) || grant.lease.expiresAt <= Date.now()) throw new Error('Agora authorization expired');
      page = p;
      await p.start(grant);
      if (g !== generation) { if (page === p) page = null; close(p); }
    } catch {
      if (page === p) page = null;
      close(p);
      if (g === generation) mode = 'agora-down';
      throw new Error('Authorized Agora voice did not start');
    }
  }
  return {
    async start() {
      if (mode !== 'sfu') throw new Error('Voice requires fresh server authorization');
      generation++; return options.startSfu();
    },
    /** Accept a destination grant only after it has arrived over the authenticated Office socket. */
    selectAuthorizedAgora: selectAgora,
    /** A server-selected Agora space is waiting for its grant. Stop any old provider immediately. */
    prepareAgora() {
      generation++; const old = page; page = null; mode = 'agora-down'; close(old); options.beforeAgora();
    },
    /** Only call after the current server's welcome selected SFU for this whole server. */
    selectSfu() { generation++; const old = page; page = null; mode = 'sfu'; close(old); },
    renewAuthorizedAgora(id: string, token: string, expiresAt: number) {
      if (mode !== 'agora' || !page) return Promise.reject(new Error('Stale Agora lease'));
      return page.renew(id, token, expiresAt);
    },
    updateAuthorizedPeers(id: string, version: number, uids: readonly UID[], peers: AgoraPageGrant['peers']) {
      if (mode !== 'agora' || !page) throw new Error('Stale Agora lease');
      page.updateAuthorizedPeers(id, version, uids, peers);
    },
    /** A lost office socket invalidates Agora; SFU retains its current reconnect behavior. */
    offline() { if (mode !== 'sfu') invalidate(); },
    stop: invalidate,
    isAgora: () => mode !== 'sfu',
    settings: () => page?.settings() ?? null,
    sttAvailable: () => page?.sttAvailable() ?? false,
    sttTap: (on: boolean) => page?.sttTap(on) ?? null,
    microphoneReady: () => page?.microphoneReady() ?? false,
    localLevel: () => page?.localLevel() ?? 0,
    remoteLevel: (id: string) => page?.remoteLevel(id) ?? 0,
    setMuted: (on: boolean) => page?.setMuted(on),
    setDevice: (id: string) => page?.setDevice(id),
    sync: () => page?.sync(),
  };
}
